import type {
    AttachmentPickers,
    ComposeRibbonControlDefProps,
    ReadOnlyRibbonControlDefProps,
} from 'owa-mail-ribbon-utils';
import type { RibbonTabDefinitionBase, RibbonTabDefinitionSet } from 'owa-acui';
import { propsBaseGlobal } from '../utils/ribbonDefinitionProps';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { owaComputedFn } from 'owa-computed-fn';
import { getComposeViewState } from 'owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState';
import getMLRHomeTab from './mlr-home-tab/getMLRHomeTab';
import getMLRNotesTab from './mlr-notes-tab/getMLRNotesTab';
import getMLRViewTab from './mlr-view-tab/getMLRViewTab';
import getMLRSearchToolsTab from './mlr-search-tab/getMLRSearchToolsTab';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isNotesScenario } from 'owa-mail-ribbon-utils/lib/isNotesScenario';
import { getRibbonMailboxInfo } from 'owa-mail-ribbon-utils/lib/getRibbonMailboxInfo';
import { getHelpTab } from 'owa-ribbon-help';
import { getSelectedTab } from 'owa-mail-ribbon-store/lib/selectors/getSelectedTab';
import type { MailboxInfo } from 'owa-client-types';
import { lazyGetMLRComposeTabs } from '../../lazyComposeFunctions';
import getComposePlaceholderTabs from '../../util/getComposePlaceholderTabs';
import getIsSearchTableShown from 'owa-mail-list-store/lib/utils/getIsSearchTableShown';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';

let composePropsGlobal: ComposeRibbonControlDefProps;
let prevAttachmentPickers: AttachmentPickers | undefined;
let prevViewState: ComposeViewState | undefined;

// Clarification: "readOnly" does not mean that this property never has its properties changed.
// "readOnly" in this context means the mail state, so "not compose".
// Importantly, mailboxInfo can change and we track that change.
let readOnlyPropsGlobal: ReadOnlyRibbonControlDefProps = {
    isPopout: false,
    mailboxInfo: undefined,
    itemId: undefined,
    projectionTabId: undefined,
    selectedRowKey: undefined,
};

export const getMailRibbonDefinitionsMLR: (
    attachmentPickers: AttachmentPickers
) => RibbonTabDefinitionSet = owaComputedFn(function getMailRibbonDefinitionsMLR(
    attachmentPickers: AttachmentPickers
): RibbonTabDefinitionSet {
    const tabs: RibbonTabDefinitionBase[] = [];

    // Check if we need to update readOnlyProps for Mailbox Info
    const mailboxInfo: MailboxInfo = getRibbonMailboxInfo(
        undefined /* RibbonControlDefinitionProps */
    );
    if (mailboxInfo !== readOnlyPropsGlobal.mailboxInfo) {
        readOnlyPropsGlobal = {
            ...readOnlyPropsGlobal,
            mailboxInfo,
        };
    }

    tabs.push(
        getMLRHomeTab(readOnlyPropsGlobal),
        getMLRViewTab(readOnlyPropsGlobal),
        getHelpTab(getSelectedTab())
    );

    const viewState: ComposeViewState | undefined = getComposeViewState();
    if (viewState?.isComposeRendered) {
        // Check if we need to update compose props
        if (
            !composePropsGlobal ||
            !prevAttachmentPickers ||
            prevAttachmentPickers !== attachmentPickers ||
            !prevViewState ||
            prevViewState !== viewState ||
            composePropsGlobal.mailboxInfo !== viewState.mailboxInfo
        ) {
            prevAttachmentPickers = attachmentPickers;
            prevViewState = viewState;
            composePropsGlobal = {
                ...propsBaseGlobal,
                composeViewState: viewState,
                attachmentPickers,
                mailboxInfo: viewState.mailboxInfo,
            };
        }

        /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
         * BASELINE. Do not copy and paste!
         *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
        const getMLRComposeTabs = lazyGetMLRComposeTabs.tryImportForRender();
        if (getMLRComposeTabs) {
            tabs.push(...getMLRComposeTabs(composePropsGlobal));
        } else {
            tabs.push(...getComposePlaceholderTabs(viewState.editorId, true /* isMlr */));
        }
    }

    if (isNotesScenario()) {
        tabs.push(getMLRNotesTab(readOnlyPropsGlobal));
    }
    // Add Search Tools tab when in search mode (but not for message suggestions - direct navigation)
    if (getIsSearchTableShown() && isFeatureEnabled('sea-searchRibbon')) {
        const tableView = getSelectedTableView();
        const scenarioType = (tableView?.tableQuery as any)?.scenarioType;
        if (scenarioType !== 'messageSuggestion') {
            tabs.push(getMLRSearchToolsTab(readOnlyPropsGlobal));
        }
    }

    return {
        tabs,
    };
});
